import { applyPatch } from 'fast-json-patch';
import { WS_HEARTBEAT_MS, type ServerMessage } from '@oss/shared';
import { tokenStore } from './api-client.js';

const WS_URL = import.meta.env['VITE_WS_URL'] ?? '/ws';

type Listener = (data: unknown) => void;

/**
 * WebSocket client implementing the snapshot + delta protocol.
 *
 * The sequence number is the whole point: a delta whose `seq` is not exactly
 * one past the last one we applied means we missed something, and applying it
 * anyway would leave the dashboard quietly showing state that never existed.
 * A gap triggers a resync instead — a brief flicker is a far better outcome
 * than a NOC screen that is confidently wrong.
 */
class RealtimeClient {
  private socket: WebSocket | null = null;
  private readonly state = new Map<string, unknown>();
  private readonly sequences = new Map<string, number>();
  private readonly listeners = new Map<string, Set<Listener>>();
  private reconnectAttempt = 0;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private closing = false;

  subscribe(topic: string, listener: Listener): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
      this.send({ op: 'subscribe', topics: [topic] });
    }
    set.add(listener);

    // Replay current state immediately so a component mounting mid-stream
    // renders data instead of an empty skeleton until the next delta.
    const current = this.state.get(topic);
    if (current !== undefined) listener(current);

    return () => {
      set.delete(listener);
      if (set.size === 0) {
        this.listeners.delete(topic);
        this.state.delete(topic);
        this.sequences.delete(topic);
        this.send({ op: 'unsubscribe', topics: [topic] });
      }
    };
  }

  connect(): void {
    if (this.socket?.readyState === WebSocket.OPEN) return;
    this.closing = false;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = WS_URL.startsWith('http') || WS_URL.startsWith('ws')
      ? WS_URL
      : `${protocol}//${window.location.host}${WS_URL}`;

    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      this.reconnectAttempt = 0;
      // The token goes in the first frame, never in the URL — query strings
      // land in proxy access logs and browser history.
      this.send({ op: 'auth', token: tokenStore.getAccess() } as never);
      const topics = [...this.listeners.keys()];
      if (topics.length > 0) this.send({ op: 'subscribe', topics });
      this.startHeartbeat();
    };

    this.socket.onmessage = (event) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data as string) as ServerMessage;
      } catch {
        return;
      }
      this.handle(message);
    };

    this.socket.onclose = () => {
      this.stopHeartbeat();
      if (!this.closing) this.scheduleReconnect();
    };

    this.socket.onerror = () => this.socket?.close();
  }

  disconnect(): void {
    this.closing = true;
    this.stopHeartbeat();
    this.socket?.close();
    this.socket = null;
  }

  private handle(message: ServerMessage): void {
    switch (message.op) {
      case 'snapshot': {
        this.state.set(message.topic, message.data);
        this.sequences.set(message.topic, message.seq);
        this.emit(message.topic, message.data);
        break;
      }
      case 'delta': {
        const expected = (this.sequences.get(message.topic) ?? 0) + 1;
        if (message.seq !== expected) {
          // Out of order or missed. Do not guess — ask for a fresh snapshot.
          this.send({ op: 'resync', topics: [message.topic] });
          return;
        }
        const current = this.state.get(message.topic);
        if (current === undefined) return;
        const next = applyPatch(structuredClone(current), message.patch as never, false, false)
          .newDocument;
        this.state.set(message.topic, next);
        this.sequences.set(message.topic, message.seq);
        this.emit(message.topic, next);
        break;
      }
      case 'gap': {
        this.send({ op: 'resync', topics: [message.topic] });
        break;
      }
      default:
        break;
    }
  }

  private emit(topic: string, data: unknown): void {
    for (const listener of this.listeners.get(topic) ?? []) listener(data);
  }

  private send(message: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = setInterval(() => this.send({ op: 'ping' }), WS_HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  /**
   * Exponential backoff with jitter, capped at 30 seconds.
   *
   * Jitter is not cosmetic: when the API restarts during a deploy, every open
   * NOC browser reconnects at once. Without jitter they would all retry on
   * the same schedule and repeatedly stampede the freshly started process.
   */
  private scheduleReconnect(): void {
    const delay = Math.min(1000 * 2 ** this.reconnectAttempt, 30_000);
    const jitter = Math.random() * 1000;
    this.reconnectAttempt += 1;
    setTimeout(() => this.connect(), delay + jitter);
  }
}

export const realtime = new RealtimeClient();
